import { supabase, formatSupabaseError } from '../../supabase';
import { formatDate, monthKey, monthLabel, lastNMonthKeys } from './ownerUtils';
import { cachedQuery } from '../cache/queryCache';

function parseDate(value) {
  if (!value) return null;
  const d = new Date(String(value).slice(0, 10) + 'T00:00:00');
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Owner finance is driven by gym_subscriptions (source of truth).
 * saas_payments is optional extras — signups/manual subs often never write there.
 */
export async function fetchFinanceData() {
  return cachedQuery('owner:finance', async () => {
  const [
    { data: subscriptions, error: subError },
    { data: payments, error: payError },
    { data: invoices, error: invError },
  ] = await Promise.all([
    supabase.from('gym_subscriptions').select('*').order('created_at', { ascending: false }),
    supabase.from('saas_payments').select('*').order('paid_on', { ascending: false }),
    supabase.from('saas_invoices').select('*').order('issued_date', { ascending: false }),
  ]);

  if (subError) {
    throw new Error(formatSupabaseError(subError, 'Could not load subscriptions.'));
  }
  // Payments / invoices tables may be empty or missing — don't block finance
  const safePayments = payError ? [] : (payments || []);
  const safeInvoices = invError ? [] : (invoices || []);
  const subs = subscriptions || [];

  const gymIds = [
    ...new Set([
      ...subs.map((s) => s.gym_id),
      ...safePayments.map((p) => p.gym_id),
      ...safeInvoices.map((i) => i.gym_id),
    ].filter(Boolean)),
  ];

  let gymsById = new Map();
  if (gymIds.length) {
    const { data: gyms } = await supabase.from('gyms').select('id, name').in('id', gymIds);
    gymsById = new Map((gyms || []).map((g) => [g.id, g.name]));
  }

  // One finance row per gym subscription (trials show as $0)
  const fromSubscriptions = subs.map((row) => {
    const isTrial = String(row.status || '').toLowerCase() === 'trial';
    const amount = isTrial ? 0 : (Number(row.price) || 0);
    const paidOn = row.start_date || String(row.created_at || '').slice(0, 10);
    const status = isTrial
      ? 'trial'
      : (row.status === 'active' || row.status === 'paid' ? 'paid' : (row.status || 'pending'));

    return {
      id: row.id,
      gymId: row.gym_id,
      gym: gymsById.get(row.gym_id) || '—',
      plan: row.plan_name || '—',
      amount,
      billingCycle: isTrial ? '—' : (row.billing_cycle || '—'),
      paidOn: formatDate(paidOn),
      paidOnRaw: paidOn,
      method: row.source === 'signup' ? 'Signup' : (row.source === 'manual' ? 'Manual' : (row.source || '—')),
      status,
      isTrial,
      source: 'subscription',
    };
  });

  const fromPayments = safePayments.map((row) => ({
    id: row.reference_code || row.id,
    gymId: row.gym_id,
    gym: gymsById.get(row.gym_id) || '—',
    plan: row.plan_name || '—',
    amount: Number(row.amount) || 0,
    billingCycle: row.billing_cycle || '—',
    paidOn: formatDate(row.paid_on),
    paidOnRaw: row.paid_on,
    method: row.payment_method || '—',
    status: row.status || 'paid',
    isTrial: false,
    source: 'payment',
  }));

  // Prefer subscription rows; append saas_payments that aren't already represented
  const subIds = new Set(fromSubscriptions.map((r) => String(r.id)));
  const extraPayments = fromPayments.filter((p) => !subIds.has(String(p.id)));
  const subscriptionPayments = [...fromSubscriptions, ...extraPayments]
    .sort((a, b) => String(b.paidOnRaw || '').localeCompare(String(a.paidOnRaw || '')));

  const outstanding = safeInvoices
    .filter((inv) => inv.status !== 'paid')
    .reduce((sum, inv) => sum + (Number(inv.amount) || 0), 0);

  const paidRows = subscriptionPayments.filter((p) => {
    const status = String(p.status || '').toLowerCase();
    return (status === 'paid' || status === 'active' || status === 'completed') && Number(p.amount) > 0;
  });

  const collected = paidRows.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const yearStart = new Date(now.getFullYear(), 0, 1);

  const monthly = paidRows
    .filter((p) => {
      const d = parseDate(p.paidOnRaw);
      return d && d >= monthStart;
    })
    .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  const annual = paidRows
    .filter((p) => {
      const d = parseDate(p.paidOnRaw);
      return d && d >= yearStart;
    })
    .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  const monthKeys = lastNMonthKeys(7);
  const revenueByMonth = Object.fromEntries(monthKeys.map((k) => [k, 0]));
  for (const p of paidRows) {
    const key = monthKey(p.paidOnRaw);
    if (key in revenueByMonth) {
      revenueByMonth[key] += Number(p.amount) || 0;
    }
  }

  return {
    subscriptionPayments,
    revenueSummary: {
      monthly,
      annual,
      outstanding,
      collected,
    },
    revenueChartData: {
      labels: monthKeys.map(monthLabel),
      values: monthKeys.map((k) => revenueByMonth[k]),
    },
  };
  }, 60_000);
}

export async function fetchDashboardData() {
  return cachedQuery('owner:dashboard', async () => {
  const [
    { count: gymCount },
    { data: subscriptions },
    finance,
  ] = await Promise.all([
    supabase.from('gyms').select('*', { count: 'exact', head: true }),
    supabase.from('gym_subscriptions').select('status, created_at, source'),
    fetchFinanceData(),
  ]);

  const subs = subscriptions || [];
  const activeStatuses = new Set(['active', 'trial']);
  const activeCount = subs.filter((s) => activeStatuses.has(s.status)).length;
  const expiredCount = subs.filter((s) => s.status === 'expired' || s.status === 'cancelled').length;

  const monthKeys = lastNMonthKeys(7);
  const signupsByMonth = Object.fromEntries(monthKeys.map((k) => [k, 0]));
  const activeByMonth = Object.fromEntries(monthKeys.map((k) => [k, 0]));
  const churnByMonth = Object.fromEntries(monthKeys.map((k) => [k, 0]));

  for (const s of subs) {
    const created = s.created_at ? monthKey(String(s.created_at).slice(0, 10)) : '';
    if (created in signupsByMonth && s.source === 'signup') {
      signupsByMonth[created] += 1;
    }
  }

  let runningActive = activeCount;
  for (let i = monthKeys.length - 1; i >= 0; i -= 1) {
    activeByMonth[monthKeys[i]] = runningActive;
    churnByMonth[monthKeys[i]] = Math.max(0, Math.round(expiredCount / monthKeys.length));
  }

  return {
    stats: [
      { label: 'Total Registered Gyms', value: String(gymCount || 0), key: 'gyms' },
      { label: 'Active Subscriptions', value: String(activeCount), key: 'subscriptions' },
      { label: 'Expired Subscriptions', value: String(expiredCount), key: 'expired', variant: 'warning' },
      { label: 'Monthly Revenue', value: finance.revenueSummary.monthly, key: 'revenue', isMoney: true },
      { label: 'Annual Revenue', value: finance.revenueSummary.annual, key: 'annual-revenue', isMoney: true },
    ],
    revenueChartData: {
      labels: finance.revenueChartData.labels,
      values: finance.revenueChartData.values,
    },
    subscriptionGrowthData: {
      labels: monthKeys.map(monthLabel),
      values: monthKeys.map((k) => activeByMonth[k]),
    },
    newSignupsData: {
      labels: monthKeys.map(monthLabel),
      values: monthKeys.map((k) => signupsByMonth[k]),
    },
    churnData: {
      labels: monthKeys.map(monthLabel),
      values: monthKeys.map((k) => churnByMonth[k]),
    },
  };
  }, 60_000);
}

export async function fetchReportsData() {
  return cachedQuery('owner:reports', async () => {
  const [{ data: subscriptions }, { data: plans }, finance] = await Promise.all([
    supabase.from('gym_subscriptions').select('*'),
    supabase.from('saas_plans').select('id, name'),
    fetchFinanceData(),
  ]);

  const planNames = Object.fromEntries((plans || []).map((p) => [p.id, p.name]));
  const activeStatuses = new Set(['active', 'trial']);
  const byPlan = {};

  for (const sub of subscriptions || []) {
    if (!activeStatuses.has(sub.status)) continue;
    // Group by real package name (trials keep their chosen plan)
    const name = sub.plan_name || planNames[sub.plan_id] || 'Unknown';
    if (!byPlan[name]) {
      byPlan[name] = {
        plan: name,
        activeCount: 0,
        monthlyCount: 0,
        quarterlyCount: 0,
        yearlyCount: 0,
        revenue: 0,
      };
    }
    byPlan[name].activeCount += 1;
    if (sub.status !== 'trial') {
      byPlan[name].revenue += Number(sub.price) || 0;
    }
    const cycle = (sub.billing_cycle || '').toLowerCase();
    if (cycle === 'monthly') byPlan[name].monthlyCount += 1;
    else if (cycle === 'quarterly') byPlan[name].quarterlyCount += 1;
    else if (cycle === 'yearly') byPlan[name].yearlyCount += 1;
  }

  const subscriptionReport = Object.values(byPlan);

  const revenueReport = finance.revenueChartData.labels.map((month, idx) => ({
    month,
    subscriptions: finance.revenueChartData.values[idx] || 0,
    renewals: 0,
    newSignups: 0,
    total: finance.revenueChartData.values[idx] || 0,
  }));

  return { subscriptionReport, revenueReport };
  }, 60_000);
}
